import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { _axios } from "../utils/axios";
import { TApplication } from "./levelThreeApplication.hooks";

export const accountantApplicationsKeyStore = {
  all: ["accountant-applications"] as const,
  lists: () => [...accountantApplicationsKeyStore.all, "list"] as const,
  list: (
    page: number,
    limit: number,
    sort: string,
    sortVal: number,
    search: string,
    statuses: string[]
  ) =>
    [
      ...accountantApplicationsKeyStore.lists(),
      { page, limit, sort, sortVal, search, statuses },
    ] as const,
  counts: (search: string, statuses: string[]) =>
    [
      ...accountantApplicationsKeyStore.all,
      "count",
      { search, statuses },
    ] as const,
};

export function useAccountantApplicationCounts({
  search,
  statuses,
}: {
  search?: string;
  statuses?: string[];
}) {
  return useQuery({
    queryKey: accountantApplicationsKeyStore.counts(
      search || "",
      statuses || []
    ),
    queryFn: () =>
      _axios
        .post<{
          success: true;
          data: number;
        }>("/accountant-payment/count", { search, statuses })
        .then((res) => res.data),
    staleTime: 0,
    select: (d) => d.data,
  });
}

export function useAccountantApplications({
  page,
  limit,
  search,
  sort,
  sortVal,
  statuses,
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  statuses?: string[];
}) {
  return useQuery({
    queryKey: accountantApplicationsKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      statuses || []
    ),
    queryFn: () =>
      _axios
        .post<{
          success: true;
          hasMore?: boolean;
          data: (TApplication & {
            receipt_no?: string;
            amount_paid?: number | string;
            payment_date?: string;
            payment_status?: string;
            receipt_file?: string;
            totalAmount?: number | string;
          })[];
        }>("/accountant-payment/list", {
          sort,
          sortVal,
          search,
          page,
          limit,
          statuses: statuses || [],
        })
        .then((res) => res.data),
    staleTime: 0,
    select: (d) => d.data,
  });
}

export function useAccountantApproveReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { applicationId: number; comment?: string }) =>
      _axios.post("/accountant-payment/approve", payload).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountantApplicationsKeyStore.all,
      });
    },
  });
}

export function useAccountantReturnReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { applicationId: number; comment?: string }) =>
      _axios.post("/accountant-payment/return", payload).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountantApplicationsKeyStore.all,
      });
    },
  });
}
